<template>
  <div class="tf">
    <Breadcrumbs main="Турниры" title="Редактировать турнир"/>

    <div class="container-fluid tf-wrap">

      <!-- ======================= верхняя панель ======================= -->
      <div class="tf-topbar">
        <button type="button" class="tf-back" @click="goBack" v-b-tooltip.hover.bottom="'Назад'">
          <i class="fa fa-arrow-left"></i>
        </button>

        <div class="tf-topbar__title">
          <div class="tf-topbar__name" :class="{ 'is-placeholder': !name }">
            {{ name || 'Турнир' }}
          </div>
          <div class="tf-topbar__meta">
            <span>ID {{ tournamentId }}</span>
            <i class="tf-dot"></i>
            <span>{{ statusLabel }}</span>
            <i class="tf-dot"></i>
            <span v-if="gameName">{{ gameName }}</span>
            <span v-else>—</span>
            <i class="tf-dot"></i>
            <span :class="isDirty ? 'text-warning' : ''">
              {{ isDirty ? 'есть несохранённые изменения' : 'изменений нет' }}
            </span>
          </div>
        </div>

        <div class="tf-topbar__spacer"></div>

        <div class="tf-topbar__actions">
          <router-link :to="{ name: 'tournament_standings', params: { standingId: tournamentId } }"
                       class="tf-icon-btn" v-b-tooltip.hover.bottom="'Сетка турнира'">
            <i class="fa fa-sitemap"></i>
          </router-link>
          <router-link :to="{ name: 'tournament_teams', params: { id: tournamentId } }"
                       class="tf-icon-btn" v-b-tooltip.hover.bottom="'Заявки и команды'">
            <i class="fa fa-users"></i>
          </router-link>
          <a v-if="publicLink" :href="publicLink" target="_blank" rel="noopener"
             class="tf-icon-btn" v-b-tooltip.hover.bottom="'Открыть на сайте'">
            <i class="fa fa-external-link"></i>
          </a>

          <div class="tf-badge" :class="isReady ? 'tf-badge--ok' : 'tf-badge--warn'">
            <i class="fa" :class="isReady ? 'fa-check' : 'fa-exclamation'"></i> {{ progress }}%
          </div>
          <button type="button" class="tf-btn-ghost" @click="goBack">Отмена</button>
          <button type="button" class="tf-btn-primary" :class="{ 'tf-spin': isSaving }"
                  :disabled="isSaving || isLoadingAllData" @click="submitForm">
            <i class="fa" :class="isSaving ? 'fa-spinner' : 'fa-check'"></i>
            Сохранить
            <kbd class="tf-kbd">{{ saveHint }}</kbd>
          </button>
        </div>
      </div>

      <!-- ======================= загрузка ======================= -->
      <div v-if="isLoadingAllData" class="tf-card">
        <div class="tf-card__body">
          <div class="tf-skeleton">
            <div v-for="n in 6" :key="'sk' + n" class="tf-skeleton__row">
              <span class="tf-sk tf-sk--label"></span>
              <span class="tf-sk"></span>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="tf-layout">

        <!-- ======================= навигация ======================= -->
        <aside class="tf-nav">
          <button v-for="section in sections" :key="section.key" type="button"
                  class="tf-nav__item" :class="{ 'is-on': activeSection === section.key }"
                  @click="goSection(section.key)">
            <i class="fa" :class="section.icon"></i>
            <span class="tf-nav__label">{{ section.label }}</span>
            <span v-if="issuesBySection[section.key]" class="tf-nav__badge">
              {{ issuesBySection[section.key].length }}
            </span>
            <span v-else-if="!section.optional" class="tf-nav__ok"><i class="fa fa-check"></i></span>
          </button>

          <div class="tf-nav__foot">
            <div class="tf-nav__progress-top">
              <span>Заполненность</span>
              <span>{{ progress }}%</span>
            </div>
            <div class="tf-nav__track">
              <div class="tf-nav__fill" :class="{ 'is-done': isReady }" :style="{ width: progress + '%' }"></div>
            </div>
          </div>
        </aside>

        <!-- ======================= контент ======================= -->
        <div class="tf-main">
          <div ref="sectionTop"></div>

          <div v-if="showProblems && formIssues.length" class="tf-alert">
            <i class="fa fa-exclamation-triangle tf-alert__icon"></i>
            <div class="tf-alert__body">
              <div class="tf-alert__title">Не заполнено обязательных полей: {{ formIssues.length }}</div>
              <div class="tf-hint">Нажмите на поле, чтобы перейти к нему.</div>
              <div class="tf-alert__list">
                <button v-for="issue in formIssues" :key="issue.label" type="button"
                        class="tf-issue" @click="goToIssue(issue)">
                  <i class="fa fa-arrow-right"></i> {{ issue.label }}
                </button>
              </div>
            </div>
            <button type="button" class="tf-alert__close" @click="showProblems = false">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <ValidationObserver ref="form" tag="div">

            <!-- ============ ОСНОВНЫЕ ============ -->
            <section v-show="activeSection === 'main'" class="tf-sec">

              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-info"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Карточка турнира</h3>
                    <p class="tf-card__sub">Быстрый доступ к связанным разделам</p>
                  </div>
                  <span class="tf-badge" :class="statusBadgeClass">{{ statusLabel }}</span>
                </div>
                <div class="tf-card__body">
                  <div class="tf-links">
                    <router-link :to="{ name: 'tournament_standings', params: { standingId: tournamentId } }" class="tf-link">
                      <i class="fa fa-sitemap"></i> Сетка и стадии
                    </router-link>
                    <router-link :to="{ name: 'tournament_teams', params: { id: tournamentId } }" class="tf-link">
                      <i class="fa fa-users"></i> Заявки и команды
                    </router-link>
                    <a v-if="publicLink" :href="publicLink" target="_blank" rel="noopener" class="tf-link">
                      <i class="fa fa-external-link"></i> Страница на сайте
                    </a>
                    <button v-if="publicLink" type="button" class="tf-link"
                            @click="copyToClipboard(publicLink, 'Ссылка скопирована')">
                      <i class="fa fa-clipboard"></i> Копировать ссылку
                    </button>
                  </div>

                  <div v-if="tournament.hash" class="tf-alert tf-alert--warn mt-3 mb-0">
                    <i class="fa fa-lock tf-alert__icon"></i>
                    <div class="tf-alert__body">
                      <div class="tf-alert__title">Турнир скрыт — доступ только по ссылке</div>
                      <div class="tf-hint" style="word-break: break-all">{{ tournamentLink }}</div>
                      <div class="tf-alert__list">
                        <button type="button" class="tf-btn-ghost"
                                @click="copyToClipboard(tournamentLink, 'Секретная ссылка скопирована')">
                          <i class="fa fa-clipboard"></i> Копировать секретную ссылку
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-sliders"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Параметры турнира</h3>
                    <p class="tf-card__sub">Дисциплина, тип и формат зафиксированы после создания</p>
                  </div>
                </div>
                <div class="tf-card__body">
                  <div class="tf-grid">

                    <div class="tf-field tf-field--8" data-field="name">
                      <label class="tf-label tf-label--req">Название турнира</label>
                      <ValidationProvider name="Название турнира" rules="min:2|required" v-slot="{ errors }">
                        <b-form-input v-model="name" placeholder="Название турнира"
                                      :state="!errors.length ? null : false"></b-form-input>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="slug">
                      <label class="tf-label">Slug</label>
                      <b-form-input type="text" v-model="currentSlug" placeholder="url-турнира"></b-form-input>
                      <div class="tf-hint">Меняется в адресе публичной страницы — старые ссылки перестанут работать.</div>
                    </div>

                    <div class="tf-field tf-field--4" data-field="game">
                      <label class="tf-label">
                        Дисциплина
                        <span class="tf-label__tag"><i class="fa fa-lock"></i> нельзя менять</span>
                      </label>
                      <b-form-select :options="games" v-model="gameId" disabled></b-form-select>
                    </div>

                    <div class="tf-field tf-field--4" data-field="variation">
                      <label class="tf-label">
                        Тип турнира
                        <span class="tf-label__tag"><i class="fa fa-lock"></i> нельзя менять</span>
                      </label>
                      <b-form-select :options="variations" v-model="currentVariation" disabled></b-form-select>
                    </div>

                    <div class="tf-field tf-field--4" data-field="format">
                      <label class="tf-label">
                        Формат
                        <span class="tf-label__tag"><i class="fa fa-lock"></i> нельзя менять</span>
                      </label>
                      <Multiselect :options="formats" v-model="currentFormats"
                                   label="text" track-by="value" disabled></Multiselect>
                    </div>

                    <div v-if="currentVariation === 4" class="tf-field tf-field--4" data-field="bpl">
                      <label class="tf-label tf-label--req">Сезон BPL</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="bplSeasons" v-model="currentBplSeasonId"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="organizer">
                      <label class="tf-label tf-label--req">Организатор</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="organizers" v-model="organizerId"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="prize">
                      <label class="tf-label tf-label--req">Призовой фонд</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-input v-model="prize" :state="!errors.length ? null : false"></b-form-input>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="slots">
                      <label class="tf-label tf-label--req">Количество команд</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-input type="number" min="2" v-model="slot_count"
                                      :state="!errors.length ? null : false"></b-form-input>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-hint">Уменьшение слотов не удаляет уже принятые заявки.</div>
                    </div>

                    <div class="tf-field tf-field--4" data-field="country">
                      <label class="tf-label">Страна</label>
                      <b-form-select v-model="currentCountry" :options="countries"></b-form-select>
                    </div>

                    <div class="tf-field tf-field--8" data-field="divisions">
                      <label class="tf-label tf-label--req">Подача для Tier</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <Multiselect :options="filteredDivisions" v-model="currentDivisions"
                                     label="text" track-by="value"
                                     :multiple="true" :hide-selected="true"
                                     placeholder="Выберите дивизионы"
                                     select-label="" selected-label="" deselect-label=""></Multiselect>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--6" data-field="youtube">
                      <label class="tf-label">Ссылка трансляции</label>
                      <b-form-input type="text" v-model="currentYoutubeLink"
                                    placeholder="https://youtube.com/..."></b-form-input>
                    </div>

                    <div class="tf-field tf-field--6" data-field="series">
                      <label class="tf-label">Серия турниров</label>
                      <b-form-input type="text" v-model="currentRelatedTournaments"
                                    placeholder="ID турниров через запятую: 120, 121"></b-form-input>
                    </div>

                    <div class="tf-field" data-field="features">
                      <label class="tf-label">Features</label>
                      <Multiselect :options="featuresList" v-model="currentFeature"
                                   label="text" track-by="value" :multiple="true"
                                   placeholder="Дополнительные возможности"
                                   select-label="" selected-label="" deselect-label=""></Multiselect>
                    </div>

                  </div>
                </div>
              </div>

              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-toggle-on"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Доступ и общение</h3>
                    <p class="tf-card__sub">Видимость турнира и чаты</p>
                  </div>
                </div>
                <div class="tf-card__body">
                  <div class="tf-toggles">
                    <div class="tf-toggle" :class="{ 'is-on': isHashTournament }">
                      <b-form-checkbox v-model="isHashTournament" switch>
                        <span class="tf-toggle__title">Только по ссылке</span>
                        <span class="tf-toggle__text">Турнир скрыт из публичного списка, доступ по секретной ссылке</span>
                      </b-form-checkbox>
                    </div>
                    <div class="tf-toggle" :class="{ 'is-on': withComments }">
                      <b-form-checkbox v-model="withComments" switch>
                        <span class="tf-toggle__title">Чат-флудилка</span>
                        <span class="tf-toggle__text">Общий чат для всех участников и зрителей</span>
                      </b-form-checkbox>
                    </div>
                    <div class="tf-toggle" :class="{ 'is-on': withCapChats }">
                      <b-form-checkbox v-model="withCapChats" switch>
                        <span class="tf-toggle__title">Кэп-чат</span>
                        <span class="tf-toggle__text">Отдельный чат капитанов команд и администрации</span>
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- ============ РАСПИСАНИЕ ============ -->
            <section v-show="activeSection === 'time'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-clock-o"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Даты и часовой пояс</h3>
                    <p class="tf-card__sub">Изменение дат сразу влияет на открытые заявки</p>
                  </div>
                </div>
                <div class="tf-card__body">
                  <div class="tf-grid">

                    <div class="tf-field tf-field--6" data-field="timezone">
                      <label class="tf-label tf-label--req">Часовой пояс</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="timezones" v-model="tournament_timezone"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="startReg">
                      <label class="tf-label tf-label--req">Начало регистрации</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <DatePicker
                            ref="editStartRegPicker"
                            mode="dateTime"
                            v-model="startRegTime"
                            is24hr
                            :model-config="modelConfig"
                            :popover="{ visibility: 'click', placement: 'bottom-start', keepVisibleOnInput: true }"
                            @popoverWillShow="handleCalendarOpen('editStartReg')"
                            @popoverWillHide="handleCalendarClose"
                        >
                          <template v-slot="{ inputValue, inputEvents }">
                            <b-form-input :value="inputValue" v-on="inputEvents"
                                          :state="!errors.length ? null : false"
                                          data-date-picker="editStartReg" readonly/>
                          </template>
                        </DatePicker>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="endReg">
                      <label class="tf-label tf-label--req">Завершение регистрации</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <DatePicker
                            ref="editEndRegPicker"
                            mode="dateTime"
                            v-model="endRegTime"
                            is24hr
                            :model-config="modelConfig"
                            :popover="{ visibility: 'click', placement: 'bottom-start', keepVisibleOnInput: true }"
                            @popoverWillShow="handleCalendarOpen('editEndReg')"
                            @popoverWillHide="handleCalendarClose"
                        >
                          <template v-slot="{ inputValue, inputEvents }">
                            <b-form-input :value="inputValue" v-on="inputEvents"
                                          :state="!errors.length ? null : false"
                                          data-date-picker="editEndReg" readonly/>
                          </template>
                        </DatePicker>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-presets">
                        <button type="button" class="tf-preset" :disabled="!startRegTime"
                                @click="endRegTime = shiftDate(startRegTime, { days: 1 })">+1 день
                        </button>
                        <button type="button" class="tf-preset" :disabled="!startRegTime"
                                @click="endRegTime = shiftDate(startRegTime, { days: 3 })">+3 дня
                        </button>
                        <button type="button" class="tf-preset" :disabled="!startRegTime"
                                @click="endRegTime = shiftDate(startRegTime, { days: 7 })">+7 дней
                        </button>
                      </div>
                    </div>

                    <div class="tf-field tf-field--4" data-field="startTournament">
                      <label class="tf-label tf-label--req">Начало турнира</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <DatePicker
                            ref="editStartTournamentPicker"
                            mode="dateTime"
                            v-model="startTime"
                            is24hr
                            :model-config="modelConfig"
                            :popover="{ visibility: 'click', placement: 'bottom-start', keepVisibleOnInput: true }"
                            @popoverWillShow="handleCalendarOpen('editStartTournament')"
                            @popoverWillHide="handleCalendarClose"
                        >
                          <template v-slot="{ inputValue, inputEvents }">
                            <b-form-input :value="inputValue" v-on="inputEvents"
                                          :state="!errors.length ? null : false"
                                          data-date-picker="editStartTournament" readonly/>
                          </template>
                        </DatePicker>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-presets">
                        <button type="button" class="tf-preset" :disabled="!endRegTime"
                                @click="startTime = shiftDate(endRegTime, { minutes: 0 })">Сразу после рег.
                        </button>
                        <button type="button" class="tf-preset" :disabled="!endRegTime"
                                @click="startTime = shiftDate(endRegTime, { hours: 1 })">+1 час
                        </button>
                        <button type="button" class="tf-preset" :disabled="!endRegTime"
                                @click="startTime = shiftDate(endRegTime, { days: 1 })">+1 день
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="tf-timeline mt-4">
                    <div class="tf-timeline__step">
                      <div class="tf-timeline__cap">Старт регистрации</div>
                      <div class="tf-timeline__val" :class="{ 'is-empty': !startRegTime }">
                        {{ startRegTime ? prettyDate(startRegTime) : 'не выбрано' }}
                      </div>
                      <div v-if="startRegTime" class="tf-timeline__sub">{{ weekday(startRegTime) }}</div>
                    </div>
                    <div class="tf-timeline__step">
                      <div class="tf-timeline__cap">Конец регистрации</div>
                      <div class="tf-timeline__val" :class="{ 'is-empty': !endRegTime }">
                        {{ endRegTime ? prettyDate(endRegTime) : 'не выбрано' }}
                      </div>
                      <div v-if="startRegTime && endRegTime" class="tf-timeline__sub">
                        окно регистрации — {{ durationHuman(startRegTime, endRegTime) }}
                      </div>
                    </div>
                    <div class="tf-timeline__step">
                      <div class="tf-timeline__cap">Старт турнира</div>
                      <div class="tf-timeline__val" :class="{ 'is-empty': !startTime }">
                        {{ startTime ? prettyDate(startTime) : 'не выбрано' }}
                      </div>
                      <div v-if="endRegTime && startTime" class="tf-timeline__sub">
                        после закрытия заявок — {{ durationHuman(endRegTime, startTime) }}
                      </div>
                    </div>
                  </div>

                  <div v-if="chronoMessage" class="tf-chrono" :class="{ 'is-ok': chronoOk }">
                    <i class="fa" :class="chronoOk ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
                    {{ chronoMessage }}
                  </div>
                </div>
              </div>
            </section>

            <!-- ============ КОНТЕНТ ============ -->
            <section v-show="activeSection === 'content'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-langbar">
                  <span class="tf-langbar__label">Язык контента</span>
                  <button v-for="lang in languages" :key="'c-lang-' + lang.id" type="button"
                          class="tf-lang" :class="{ 'is-on': language === lang.language.toLowerCase() }"
                          @click="getLanguageFromBtn(lang.language.toLowerCase())">
                    <img :src="lang.img" :alt="lang.language">
                    {{ lang.language }}
                    <span class="tf-lang__state"
                          :class="contentFilled(lang.language.toLowerCase()) ? 'is-filled' : 'is-empty'"></span>
                  </button>
                  <span class="tf-hint ml-auto mt-0">Обязателен только русский — остальные языки по желанию</span>
                </div>

                <div class="tf-card__body">
                  <div class="tf-field mb-4" data-field="schedule">
                    <label class="tf-label" :class="{ 'tf-label--req': language === 'ru' }">
                      Расписание
                      <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                    </label>
                    <div class="tf-editor">
                      <template v-for="lang in Object.keys(schedule)">
                        <SunEditor :key="lang + '-editSchedule'" v-if="language === lang"
                                   v-model="schedule[lang]" :editorId="'editSchedule'"/>
                      </template>
                    </div>
                    <div v-if="!hasText(schedule.ru)" class="tf-err">
                      <i class="fa fa-exclamation-circle"></i> Расписание на русском — обязательное поле
                    </div>
                  </div>

                  <div class="tf-field mb-4" data-field="description">
                    <label class="tf-label" :class="{ 'tf-label--req': language === 'ru' }">
                      Описание
                      <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                    </label>
                    <div class="tf-editor">
                      <template v-for="lang in Object.keys(description)">
                        <SunEditor :key="lang + '-editDescription'" v-if="language === lang"
                                   v-model="description[lang]" :editorId="'editDescription'"/>
                      </template>
                    </div>
                    <div v-if="!hasText(description.ru)" class="tf-err">
                      <i class="fa fa-exclamation-circle"></i> Описание на русском — обязательное поле
                    </div>
                  </div>

                  <div class="tf-field" data-field="rules">
                    <label class="tf-label" :class="{ 'tf-label--req': language === 'ru' }">
                      Правила
                      <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                    </label>
                    <div class="tf-editor">
                      <template v-for="lang in Object.keys(rules)">
                        <SunEditor :key="lang + '-editRules'" v-if="language === lang"
                                   v-model="rules[lang]" :editorId="'editRules'"/>
                      </template>
                    </div>
                    <div v-if="!hasText(rules.ru)" class="tf-err">
                      <i class="fa fa-exclamation-circle"></i> Правила на русском — обязательное поле
                    </div>
                  </div>
                </div>
              </div>

              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-picture-o"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Обложки</h3>
                    <p class="tf-card__sub">Новый файл заменит текущую обложку после сохранения</p>
                  </div>
                </div>
                <div class="tf-card__body">
                  <div class="tf-media">

                    <div class="tf-drop" :class="{ 'is-over': dragTarget === 'desktop' }"
                         @dragover.prevent="dragTarget = 'desktop'"
                         @dragleave.prevent="dragTarget = null"
                         @drop.prevent="onCoverDrop($event, 'desktop')">
                      <div class="tf-drop__head">
                        <i class="fa fa-desktop"></i> Десктопная обложка
                        <span class="tf-drop__pick" @click="pickFile('coverInput')">Выбрать файл</span>
                      </div>
                      <div v-if="coverPreview" class="tf-drop__preview">
                        <img :src="coverPreview" alt="Новая обложка">
                        <span class="tf-drop__badge">Будет загружена</span>
                        <button type="button" class="tf-drop__remove" @click="clearCover('desktop')">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                      <div v-else-if="tournamentCurrentImage" class="tf-drop__preview" @click="pickFile('coverInput')">
                        <img :src="tournamentCurrentImage" alt="Текущая обложка">
                        <span class="tf-drop__badge">Текущая</span>
                      </div>
                      <div v-else class="tf-drop__zone" @click="pickFile('coverInput')">
                        <i class="fa fa-cloud-upload fa-2x"></i>
                        <span>Перетащите изображение сюда</span>
                      </div>
                      <div v-if="coverMeta" class="tf-drop__file">{{ coverMeta.name }} · {{ coverMeta.size }}</div>
                      <input ref="coverInput" type="file" accept="image/*" @change="onCoverChange($event, 'desktop')">
                    </div>

                    <div class="tf-drop" :class="{ 'is-over': dragTarget === 'mobile' }"
                         @dragover.prevent="dragTarget = 'mobile'"
                         @dragleave.prevent="dragTarget = null"
                         @drop.prevent="onCoverDrop($event, 'mobile')">
                      <div class="tf-drop__head">
                        <i class="fa fa-mobile"></i> Мобильная обложка
                        <span class="tf-drop__pick" @click="pickFile('mobileInput')">Выбрать файл</span>
                      </div>
                      <div v-if="mobilePreview" class="tf-drop__preview">
                        <img :src="mobilePreview" alt="Новая мобильная обложка">
                        <span class="tf-drop__badge">Будет загружена</span>
                        <button type="button" class="tf-drop__remove" @click="clearCover('mobile')">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                      <div v-else-if="tournamentCurrentMobileImage" class="tf-drop__preview" @click="pickFile('mobileInput')">
                        <img :src="tournamentCurrentMobileImage" alt="Текущая мобильная обложка">
                        <span class="tf-drop__badge">Текущая</span>
                      </div>
                      <div v-else class="tf-drop__zone" @click="pickFile('mobileInput')">
                        <i class="fa fa-cloud-upload fa-2x"></i>
                        <span>Перетащите изображение сюда</span>
                      </div>
                      <div v-if="mobileMeta" class="tf-drop__file">{{ mobileMeta.name }} · {{ mobileMeta.size }}</div>
                      <input ref="mobileInput" type="file" accept="image/*" @change="onCoverChange($event, 'mobile')">
                    </div>

                  </div>
                </div>
              </div>
            </section>

            <!-- ============ ОЧКИ ЗА МЕСТО ============ -->
            <section v-show="activeSection === 'points'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-table"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Очки за место</h3>
                    <p class="tf-card__sub">Начисление очков по занятому месту в матче</p>
                  </div>
                  <span class="tf-badge">{{ tournamentPlaces.length }} мест</span>
                </div>
                <div class="tf-card__body">
                  <AddTournamentPlace></AddTournamentPlace>
                </div>
              </div>
            </section>

            <!-- ============ ПРИЗЫ ============ -->
            <section v-show="activeSection === 'prizes'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-trophy"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Призовой фонд по местам</h3>
                    <p class="tf-card__sub">Распределение призовых между призёрами</p>
                  </div>
                  <span class="tf-badge">{{ tournamentPrizes.length }} мест</span>
                </div>
                <div class="tf-card__body">
                  <AddTournamentPrize/>
                </div>
              </div>
            </section>

            <!-- ============ SEO ============ -->
            <section v-show="activeSection === 'meta'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-langbar">
                  <span class="tf-langbar__label">Язык мета-данных</span>
                  <button v-for="lang in languages" :key="'m-lang-' + lang.id" type="button"
                          class="tf-lang" :class="{ 'is-on': language === lang.language.toLowerCase() }"
                          @click="getLanguageFromBtn(lang.language.toLowerCase())">
                    <img :src="lang.img" :alt="lang.language">
                    {{ lang.language }}
                    <span class="tf-lang__state"
                          :class="metaFilled(lang.language.toLowerCase()) ? 'is-filled' : 'is-empty'"></span>
                  </button>
                </div>
                <div class="tf-card__body">
                  <div class="tf-grid">
                    <div class="tf-field" data-field="metaTitle">
                      <label class="tf-label">
                        Мета-заголовок
                        <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                      </label>
                      <b-form-input v-model="metaTitle[language]" placeholder="Title для поисковиков"></b-form-input>
                      <div class="tf-hint">{{ (metaTitle[language] || '').length }} символов — оптимально до 60</div>
                    </div>
                    <div class="tf-field" data-field="metaDescription">
                      <label class="tf-label">
                        Мета-описание
                        <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                      </label>
                      <b-textarea v-model="metaDescription[language]" rows="4"
                                  placeholder="Description для поисковиков"></b-textarea>
                      <div class="tf-hint">{{ (metaDescription[language] || '').length }} символов — оптимально до 160</div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </ValidationObserver>

          <div class="tf-savebar">
            <button type="button" class="tf-btn-ghost" @click="goBack">Отмена</button>
            <button type="button" class="tf-btn-primary" :disabled="isSaving" @click="submitForm">
              <i class="fa fa-check"></i> Сохранить
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {TournamentAdapterMixin} from "../../mixins/Tournament/TournamentAdapterMixin";
import {LanguageAdapterMixin} from "../../mixins/Language/LanguageAdapterMixin";
import {TournamentFormUxMixin} from "@/mixins/Tournament/TournamentFormUxMixin";
import {DatePicker} from 'v-calendar';
import Multiselect from "vue-multiselect";
import {FeaturesAdapterMixin} from "@/mixins/Features/FeaturesAdapterMixin";
import AddTournamentPlace from "@/pages/Tournament/AddTournamentPlace.vue";
import SunEditor from "@/components/SunEditor.vue";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import AddTournamentPrize from './AddTournamentPrize.vue';

const STATUS_LABELS = {
  pending: "Регистрация скоро",
  registration: "Идёт регистрация",
  game: "Идёт игра",
  ended: "Завершён",
};

export default {
  mixins: [TournamentAdapterMixin, LanguageAdapterMixin, FeaturesAdapterMixin, TournamentFormUxMixin],
  name: "EditPageTournament",
  components: {
    SunEditor,
    AddTournamentPlace,
    Multiselect,
    DatePicker,
    ValidationObserver,
    ValidationProvider,
    AddTournamentPrize,
  },
  data() {
    return {
      isLoadingAllData: true,
      modelConfig: {
        type: "string",
        mask: "YYYY-MM-DD HH:mm:ss",
      },
      dateMask: "YYYY-MM-DD HH:mm:ss",
      isHashTournament: false,
      activeCalendar: null,
      snapshot: null,
    };
  },
  computed: {
    sections() {
      return [
        {key: 'main', label: 'Основные', icon: 'fa-sliders'},
        {key: 'time', label: 'Расписание', icon: 'fa-clock-o'},
        {key: 'content', label: 'Контент', icon: 'fa-file-text-o'},
        {key: 'points', label: 'Очки за место', icon: 'fa-table', optional: true},
        {key: 'prizes', label: 'Призовой фонд', icon: 'fa-trophy', optional: true},
        {key: 'meta', label: 'SEO / мета', icon: 'fa-search', optional: true},
      ];
    },

    formIssues() {
      if (this.isLoadingAllData) return [];
      const issues = [];
      const add = (condition, section, label, field) => {
        if (condition) issues.push({section, label, field});
      };

      add(!this.name || String(this.name).trim().length < 2, 'main', 'Название турнира', 'name');
      add(!this.organizerId, 'main', 'Организатор', 'organizer');
      add(this.prize === '' || this.prize === null || this.prize === undefined, 'main', 'Призовой фонд', 'prize');
      add(!this.slot_count, 'main', 'Количество команд', 'slots');
      add(this.currentVariation === 4 && !this.currentBplSeasonId, 'main', 'Сезон BPL', 'bpl');
      add(!this.currentDivisions || !this.currentDivisions.length, 'main', 'Подача для Tier', 'divisions');

      add(!this.tournament_timezone, 'time', 'Часовой пояс', 'timezone');
      add(!this.startRegTime, 'time', 'Начало регистрации', 'startReg');
      add(!this.endRegTime, 'time', 'Завершение регистрации', 'endReg');
      add(!this.startTime, 'time', 'Начало турнира', 'startTournament');

      add(!this.hasText(this.schedule.ru), 'content', 'Расписание RU', 'schedule');
      add(!this.hasText(this.description.ru), 'content', 'Описание RU', 'description');
      add(!this.hasText(this.rules.ru), 'content', 'Правила RU', 'rules');

      return issues;
    },

    requiredCount() {
      return this.currentVariation === 4 ? 13 : 12;
    },

    tournamentId() {
      return this.$route.params.id;
    },

    filteredDivisions() {
      return this.divisions || [];
    },

    gameName() {
      const game = (this.games || []).find(item => String(item.value) === String(this.gameId));
      return game ? game.text : '';
    },

    statusLabel() {
      const code = this.currentStatus && this.currentStatus.text;
      return STATUS_LABELS[code] || code || '—';
    },

    statusBadgeClass() {
      const code = this.currentStatus && this.currentStatus.text;
      if (code === 'registration') return 'tf-badge--ok';
      if (code === 'game') return 'tf-badge--warn';
      if (code === 'ended') return '';
      return 'tf-badge--primary';
    },

    baseSiteUrl() {
      return process.env.NODE_ENV === "production"
          ? "https://bigplay.gg/ru/esports-mobile-tournaments/"
          : "https://client.bigplay.gg/ru/esports-mobile-tournaments/";
    },

    publicLink() {
      if (!this.tournament || !this.tournament.slug) return '';
      return `${this.baseSiteUrl}${this.tournament.slug}/`;
    },

    tournamentLink() {
      if (this.tournament && this.tournament.hash) {
        return `${this.baseSiteUrl}${this.tournament.slug}/?hash=${this.tournament.hash}`;
      }
      return "";
    },

    chronoOk() {
      if (!this.startRegTime || !this.endRegTime || !this.startTime) return false;
      const a = this.parseDate(this.startRegTime);
      const b = this.parseDate(this.endRegTime);
      const c = this.parseDate(this.startTime);
      return a < b && b <= c;
    },

    chronoMessage() {
      const a = this.parseDate(this.startRegTime);
      const b = this.parseDate(this.endRegTime);
      const c = this.parseDate(this.startTime);
      if (!a || !b || !c) return '';
      if (a >= b) return 'Регистрация заканчивается раньше, чем начинается — проверьте даты.';
      if (c < b) return 'Турнир стартует до закрытия регистрации — проверьте даты.';
      return 'Порядок дат корректный.';
    },

    saveHint() {
      return /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘S' : 'Ctrl+S';
    },

    /** Снимок редактируемых значений — по нему определяем несохранённые изменения. */
    currentSnapshot() {
      return JSON.stringify({
        name: this.name,
        slug: this.currentSlug,
        organizerId: this.organizerId,
        prize: this.prize,
        slots: this.slot_count,
        bpl: this.currentBplSeasonId,
        country: this.currentCountry,
        divisions: (this.currentDivisions || []).map(i => i.value),
        features: (this.currentFeature || []).map(i => i.value),
        youtube: this.currentYoutubeLink,
        related: this.currentRelatedTournaments,
        timezone: this.tournament_timezone,
        startReg: this.startRegTime,
        endReg: this.endRegTime,
        start: this.startTime,
        hash: this.isHashTournament,
        comments: this.withComments,
        capChats: this.withCapChats,
        schedule: this.schedule,
        description: this.description,
        rules: this.rules,
        metaTitle: this.metaTitle,
        metaDescription: this.metaDescription,
      });
    },

    isDirty() {
      if (this.isLoadingAllData || !this.snapshot) return false;
      if (this.newImage || this.newMobileImage) return true;
      return this.snapshot !== this.currentSnapshot;
    },
  },
  async created() {
    this.isLoadingAllData = true;

    await this.grabGames();
    await this.indexTimeZones();
    await this.grabOrganizers();
    await this.loadVariations();
    await this.grabBplSeasons();

    // Турнир грузим первым — из него берём game_id для форматов и дивизионов
    await this.grabTournament(this.$route.params.id);

    let gameSlug = "mlbb";
    if (this.tournament.game_id === 1) {
      gameSlug = "pubg-mobile";
    } else if (this.tournament.game_id === 3) {
      gameSlug = "mlbb";
    }

    await this.grabDivisionsList({slug: gameSlug});
    await this.indexFormat({gameId: this.tournament.game_id});

    await this.getCountriesQuery();
    await this.grabFeaturesList();

    this.isLoadingAllData = false;
    this.isHashTournament = !!this.tournament.hash;
    this.newVariation = this.getTournamentVariationId;
    this.currentBplSeasonId = this.tournament.bpl_season_id || "";

    this.$nextTick(() => {
      this.snapshot = this.currentSnapshot;
    });

    this.setupGlobalClickHandler();
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
        return;
      }
      this.$router.push({name: 'tournaments'});
    },

    contentFilled(lang) {
      return this.hasText(this.description[lang]) || this.hasText(this.rules[lang]) || this.hasText(this.schedule[lang]);
    },

    metaFilled(lang) {
      return Boolean((this.metaTitle[lang] || '').length || (this.metaDescription[lang] || '').length);
    },

    submitForm() {
      if (this.isSaving || this.isLoadingAllData) return;

      if (this.formIssues.length) {
        this.showProblems = true;
        this.goSection(this.formIssues[0].section);
        this.$toasted.error(`Не заполнено: ${this.formIssues.length}`, {position: 'top-right', duration: 3000});
        if (this.$refs.form && this.$refs.form.validate) this.$refs.form.validate();
        return;
      }

      this.showProblems = false;
      this.isSaving = true;
      this.suppressGuard = true;
      this.updateCurrentTournament();
      setTimeout(() => {
        this.isSaving = false;
      }, 4000);
    },

    showImage(event) {
      const file = event.target.files && event.target.files[0];
      if (file) this.applyCoverFile(file, 'desktop');
    },

    showMobileImage(event) {
      const file = event.target.files && event.target.files[0];
      if (file) this.applyCoverFile(file, 'mobile');
    },

    // ------------------------------------------------ календари
    setupGlobalClickHandler() {
      this.globalClickHandler = (event) => {
        const isInsideCalendar = event.target.closest('.vc-container') ||
            event.target.closest('.vc-popover-content-wrapper') ||
            event.target.closest('[data-date-picker]') ||
            event.target.closest('.vc-popover-trigger');

        if (!isInsideCalendar && this.activeCalendar) {
          setTimeout(() => {
            this.activeCalendar = null;
          }, 50);
        }
      };

      document.addEventListener('click', this.globalClickHandler, {capture: false});
    },

    handleCalendarOpen(calendarName) {
      this.closeOtherCalendars(calendarName);
      this.activeCalendar = calendarName;
    },

    handleCalendarClose() {
      this.activeCalendar = null;
    },

    closeOtherCalendars(currentCalendar) {
      const calendars = {
        editStartReg: 'editStartRegPicker',
        editEndReg: 'editEndRegPicker',
        editStartTournament: 'editStartTournamentPicker'
      };

      Object.keys(calendars).forEach(calendarKey => {
        if (calendarKey !== currentCalendar) {
          const pickerRef = calendars[calendarKey];
          if (this.$refs[pickerRef] && this.$refs[pickerRef].hidePopover) {
            try {
              this.$refs[pickerRef].hidePopover();
            } catch (error) {
              console.warn('Не удалось закрыть календарь:', pickerRef);
            }
          }
        }
      });
    },
  },

  async beforeRouteLeave(to, from, next) {
    if (this.suppressGuard || !this.isDirty) {
      next();
      return;
    }
    const ok = await this.confirmLeave();
    next(ok === true);
  },

  beforeDestroy() {
    if (this.globalClickHandler) {
      document.removeEventListener('click', this.globalClickHandler);
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
@import "~@/assets/scss/tournament-form.scss";

/* Календарь всегда поверх карточек формы */
::v-deep .vc-container {
  position: relative;
  z-index: 1050;
}

::v-deep [data-date-picker] {
  cursor: pointer;
  user-select: none;
}

/* скелетон загрузки */
.tf-skeleton__row {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--tf-border);

  &:last-child { border-bottom: 0; }
}

.tf-sk {
  height: 14px;
  border-radius: 7px;
  background: linear-gradient(90deg, var(--tf-soft) 25%, var(--tf-soft-2) 37%, var(--tf-soft) 63%);
  background-size: 400% 100%;
  animation: tf-shimmer 1.3s ease infinite;

  &--label { height: 12px; }
}

@keyframes tf-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
</style>
